import React from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  AccordionSet,
  FilterAccordionHeader,
  Select,
} from '@folio/stripes-components';
import { CheckboxFilter, MultiSelectionFilter } from '@folio/stripes/smart-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { getLocaleLabel } from '../../utils/utils';

const ACCORDION_ID_PREFIX = 'translation-filter-accordion';

/**
 * TranslationFilters — follows ui-users Filters.js pattern exactly.
 *
 * Receives onChangeHandlers from SearchAndSortQuery:
 *  - onChangeHandlers.state({ ...activeFilters, [name]: values })  → updates filters
 *  - onChangeHandlers.clearGroup(name)                              → clears one filter
 *
 * SearchAndSortQuery handles URL writing automatically.
 *
 * Locale  → Select (single value, mandatory)
 * App     → MultiSelectionFilter (searchable)
 * Table   → MultiSelectionFilter (searchable, depends on App)
 * Status  → CheckboxFilter (multi)
 */
function TranslationFilters({
  activeFilters = {},
  onChangeHandlers,
  resultOffset,
  languages = [],
  apps = [],
  tables = [],
  activeFilterKeys = ['locale', 'app', 'table', 'status'],
}) {
  const intl = useIntl();

  const handleFilterChange = (filter) => {
    if (resultOffset) resultOffset.replace(0);
    onChangeHandlers.state({
      ...activeFilters,
      [filter.name]: filter.values,
    });
  };

  const handleLocaleChange = (e) => {
    if (resultOffset) resultOffset.replace(0);
    onChangeHandlers.state({
      ...activeFilters,
      localeValue: e.target.value ? [e.target.value] : [],
      appName:     [],
      tableName:   [],
    });
  };

  const handleClearGroup = (name) => {
    if (resultOffset) resultOffset.replace(0);
    onChangeHandlers.clearGroup(name);
  };

  const {
    status    = [],
    appName   = [],
    tableName = [],
    localeValue = [],
  } = activeFilters;

  const currentLocale = Array.isArray(localeValue) ? (localeValue[0] || '') : (localeValue || '');

  // ── Locale options ────────────────────────────────────────────────
  const localeOptions = [
    {
      value: '',
      label: intl.formatMessage({
        id: 'ui-translations.filters.LocaleFilter.selectuserLocales',
        defaultMessage: 'Select language…',
      }),
    },
    ...languages.map((lang) => ({
      value: lang.localeValue,
      label: getLocaleLabel(lang.localeValue, intl),
    })),
  ];

  // ── App options ───────────────────────────────────────────────────
  const appOptions = apps.map((app) => ({
    value: app,
    label: app.startsWith('ui-')
      ? intl.formatMessage({ id: `${app}.meta.title`, defaultMessage: app.replace('ui-', '') })
      : intl.formatMessage({ id: `ui-translations.appName.${app}`, defaultMessage: app }),
  }));

  // ── Table options ─────────────────────────────────────────────────
  const tableOptions = tables.map((tbl) => ({
    value: tbl,
    label: intl.formatMessage({
      id: `ui-translations.filters.TableFilter.${tbl}`,
      defaultMessage: tbl,
    }),
  }));

  // ── Status options ────────────────────────────────────────────────
  const statusOptions = [
    {
      value: 'translated',
      label: intl.formatMessage({
        id: 'ui-translations.filters.TranslationStatusFilter.translated',
        defaultMessage: 'Translated',
      }),
    },
    {
      value: 'notTranslated',
      label: intl.formatMessage({
        id: 'ui-translations.filters.TranslationStatusFilter.notTranslated',
        defaultMessage: 'Not translated',
      }),
    },
    {
      value: 'sameAsSource',
      label: intl.formatMessage({
        id: 'ui-translations.filters.TranslationStatusFilter.sameAsSource',
        defaultMessage: 'Same as source',
      }),
    },
  ];

  return (
    <AccordionSet>

      {/* Locale — single Select */}
      {activeFilterKeys.includes('locale') && (
        <Accordion
          id={`${ACCORDION_ID_PREFIX}-locale`}
          header={FilterAccordionHeader}
          label={<FormattedMessage id="ui-translations.filters.LocaleFilter.Accordion" defaultMessage="Language" />}
          separator={false}
          displayClearButton={!!currentLocale}
          onClearFilter={() => handleClearGroup('localeValue')}
        >
          <Select
            id="translation-locale-select"
            dataOptions={localeOptions}
            value={currentLocale}
            onChange={handleLocaleChange}
            marginBottom0
          />
        </Accordion>
      )}

      {/* App — MultiSelectionFilter (searchable) */}
      {activeFilterKeys.includes('app') && (
        <Accordion
          id={`${ACCORDION_ID_PREFIX}-app`}
          header={FilterAccordionHeader}
          label={<FormattedMessage id="ui-translations.filters.AppFilter.Accordion" defaultMessage="Application" />}
          separator={false}
          displayClearButton={appName.length > 0}
          onClearFilter={() => handleClearGroup('appName')}
        >
          <MultiSelectionFilter
            id="translation-app-filter"
            dataOptions={appOptions}
            name="appName"
            selectedValues={appName}
            onChange={(filter) => {
              if (resultOffset) resultOffset.replace(0);
              onChangeHandlers.state({
                ...activeFilters,
                appName:   filter.values,
                tableName: [],
              });
            }}
            disabled={!currentLocale}
          />
        </Accordion>
      )}

      {/* Table — MultiSelectionFilter (searchable) */}
      {activeFilterKeys.includes('table') && (
        <Accordion
          id={`${ACCORDION_ID_PREFIX}-table`}
          header={FilterAccordionHeader}
          label={<FormattedMessage id="ui-translations.filters.TableFilter.Accordion" defaultMessage="Table" />}
          separator={false}
          displayClearButton={tableName.length > 0}
          onClearFilter={() => handleClearGroup('tableName')}
        >
          <MultiSelectionFilter
            id="translation-table-filter"
            dataOptions={tableOptions}
            name="tableName"
            selectedValues={tableName}
            onChange={handleFilterChange}
            disabled={!appName.length}
          />
        </Accordion>
      )}

      {/* Status — CheckboxFilter */}
      {activeFilterKeys.includes('status') && (
        <Accordion
          id={`${ACCORDION_ID_PREFIX}-status`}
          header={FilterAccordionHeader}
          label={<FormattedMessage id="ui-translations.filters.TranslationStatusFilter.Accordion" defaultMessage="Status" />}
          separator={false}
          displayClearButton={status.length > 0}
          onClearFilter={() => handleClearGroup('status')}
        >
          <CheckboxFilter
            dataOptions={statusOptions}
            name="status"
            selectedValues={status}
            onChange={handleFilterChange}
          />
        </Accordion>
      )}

    </AccordionSet>
  );
}

TranslationFilters.propTypes = {
  activeFilters:    PropTypes.object,
  onChangeHandlers: PropTypes.object.isRequired,
  resultOffset:     PropTypes.shape({ replace: PropTypes.func.isRequired }),
  languages:        PropTypes.arrayOf(PropTypes.object),
  apps:             PropTypes.arrayOf(PropTypes.string),
  tables:           PropTypes.arrayOf(PropTypes.string),
  activeFilterKeys: PropTypes.arrayOf(PropTypes.string),
};

export default TranslationFilters;
